import { Badge, Flex, IconButton, Skeleton, Text } from "@radix-ui/themes";
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from "@radix-ui/react-icons";
import { EmptyCell, getValue } from "../explore/CellGenerator";
import { ColumnType } from "../explore/ExploreTable";
import {
  getArticleSource,
  articleSourceLabel,
  articleSourceColor,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";

export interface ArticleRow {
  articleId: string;
  title: string;
  status: string;
  ticketTrend: number;
  relatedTickets?: number | null;
  url: string;
  external: boolean;
  updatedAt: string;
  content: string;
  accessLabels?: string[];
}

export const KnowledgeGapsColumns: ColumnType<keyof ArticleRow | "actions">[] =
  [
    {
      key: "title",
      label: "Articles",
      minWidth: "50%",
    },
    {
      key: "ticketTrend",
      label: "Ticket Trend",
    },
    {
      key: "updatedAt",
      label: "Updated At",
    },
    {
      key: "actions",
      label: "",
      minWidth: "50px",
      maxWidth: "50px",
    },
  ];

export function articlesCellGenerator(
  values: ArticleRow,
  { key }: ColumnType<string | number | symbol>,
  groupBy?:
    | "topics"
    | "categories"
    | "teams"
    | "agents"
    | "customers"
    | "tenantUser"
    | "intents",
  onDelete?: (articleId: string, articleTitle: string) => void
): JSX.Element {
  if (key === "title") {
    return (
      <Flex direction={"column"} align={"start"} gap={"0"}>
        <Flex align={"center"} gap={"2"}>
          <Text size={"2"} weight={"regular"}>
            {getValue(values, key as keyof ArticleRow)}
          </Text>
          <Badge
            size={"1"}
            color={
              values.external
                ? (articleSourceColor[getArticleSource(values.url)] as any)
                : "red"
            }
            variant="soft"
          >
            {values.external
              ? articleSourceLabel[getArticleSource(values.url)]
              : "Internal"}
          </Badge>
        </Flex>

        {values.relatedTickets === undefined ? (
          <Text size={"2"} color="gray" weight={"regular"}>
            <Skeleton>related tickets in selected period</Skeleton>
          </Text>
        ) : (
          <Text size={"2"} color="gray" weight={"regular"}>
            {values.relatedTickets} related tickets in selected period
          </Text>
        )}
      </Flex>
    );
  }

  if (key === "ticketTrend") {
    return values.relatedTickets === undefined ? (
      <Skeleton>ticket trend</Skeleton>
    ) : (
      <ArticleTicketsTrend ticketTrend={values.ticketTrend} />
    );
  }

  if (key === "actions") {
    return (
      <Flex justify="end">
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          onClick={(e) => {
            e.stopPropagation();
            if (onDelete) {
              onDelete(values.articleId, values.title);
            }
          }}
        >
          <TrashIcon />
        </IconButton>
      </Flex>
    );
  }

  const value = getValue(values, key as keyof ArticleRow);
  return <>{value != null ? value : <EmptyCell />}</>;
}

export function articlesCellValueGetter(
  values: ArticleRow,
  key: keyof ArticleRow | "actions"
): string | number | boolean | undefined {
  if (key === "actions") {
    return undefined;
  }

  if (key === "title") {
    return getValue(values, "relatedTickets") ?? undefined;
  }

  if (key === "accessLabels") {
    return undefined;
  }

  const value = getValue(values, key as keyof ArticleRow);
  if (Array.isArray(value)) {
    return undefined;
  }
  return value != null ? value : undefined;
}

export const ArticleTicketsTrend = ({
  ticketTrend,
}: {
  ticketTrend: number;
}) => {
  return (
    <Flex align={"center"}>
      <Text size={"2"} weight={"regular"} style={{ width: "32px" }}>
        {Math.abs(ticketTrend)}
      </Text>
      {ticketTrend > 0 ? (
        <ArrowUpIcon />
      ) : ticketTrend < 0 ? (
        <ArrowDownIcon />
      ) : (
        <></>
      )}
    </Flex>
  );
};
